import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  X,
  ArrowRight,
  MessageSquare,
  Clock,
  DollarSign,
  Shield,
  Target,
  Sparkles,
  AlertCircle,
  Zap
} from 'lucide-react';

const comparisonData = [
  { feature: 'Available 24/7 instantly', vzmo: true, betterhelp: false },
  { feature: 'No scheduling required', vzmo: true, betterhelp: false },
  { feature: 'Remembers all conversations', vzmo: true, betterhelp: true },
  { feature: 'Goal setting & tracking', vzmo: true, betterhelp: false },
  { feature: 'Action plan creation', vzmo: true, betterhelp: false },
  { feature: 'Weekly progress reports', vzmo: true, betterhelp: false },
  { feature: 'Private journaling', vzmo: true, betterhelp: false },
  { feature: 'Licensed therapist', vzmo: false, betterhelp: true },
  { feature: 'Clinical mental health treatment', vzmo: false, betterhelp: true },
  { feature: 'Cancel anytime, no commitment', vzmo: true, betterhelp: true },
  { feature: 'Under $10/month option', vzmo: true, betterhelp: false },
  { feature: 'Unlimited conversations', vzmo: true, betterhelp: false },
];

const testimonials = [
  {
    quote: "BetterHelp was helpful but expensive and I had to wait for appointments. VZMO gives me instant support whenever I need to think something through.",
    author: "Jennifer K.",
    role: "Teacher",
    note: "Uses VZMO for daily support"
  },
  {
    quote: "I needed therapy for a while, but now I'm in a better place and just need someone to help me stay on track. VZMO is perfect for that.",
    author: "Marcus T.",
    role: "Consultant",
    note: "Transitioned from therapy to coaching"
  },
  {
    quote: "At $300/month, BetterHelp was too much for what I needed. VZMO costs a fraction and helps me with my actual day-to-day decisions.",
    author: "Priya S.",
    role: "Startup Founder",
    note: "Switched to save money"
  }
];

const useCases = [
  {
    icon: Target,
    title: "Goal Achievement",
    description: "Career decisions, habit building, project planning, and staying accountable to your personal goals."
  },
  {
    icon: MessageSquare,
    title: "Decision Making",
    description: "Working through tough choices about jobs, relationships, finances, and life direction with a thinking partner."
  },
  {
    icon: Clock,
    title: "Daily Support",
    description: "Processing thoughts, getting unstuck, and maintaining mental clarity without scheduling appointments."
  },
  {
    icon: Shield,
    title: "Private Reflection",
    description: "Journaling with AI insights in a completely private space that's always available."
  }
];

export function BetterHelpAlternativePage() {
  useEffect(() => {
    document.title = 'BetterHelp Alternative for Coaching | VZMO';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Looking for a BetterHelp alternative that costs less? VZMO offers AI life coaching at a fraction of the price - instant access, no scheduling, 24/7 availability.');
    }
  }, []);

  return (
    <div className="bg-white pt-20">
      <section className="py-16 sm:py-24 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="inline-block text-coral-400 font-medium text-sm tracking-wide uppercase mb-4">
              BetterHelp Alternative
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
              Coaching, not therapy.
              <span className="block text-coral-400">A fraction of the cost.</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed mb-8">
              Not everyone needs therapy. If you're looking for guidance on goals, decisions, and
              personal growth, AI coaching might be exactly what you need - at a price that makes sense.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center px-8 py-4 bg-coral-500 text-white rounded-full font-semibold text-lg hover:bg-coral-600 transition-all shadow-lg"
              >
                Try VZMO free for 7 days
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a
                href="#when-to-use"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all"
              >
                Is coaching right for me?
              </a>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {[
              { icon: Clock, label: '24/7 Available', desc: 'No scheduling needed' },
              { icon: DollarSign, label: 'From $9.99/mo', desc: 'Cancel anytime' },
              { icon: Zap, label: 'Instant Response', desc: 'No waiting for replies' }
            ].map((item, index) => (
              <div key={index} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 text-center">
                <div className="w-12 h-12 rounded-xl bg-coral-500/20 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-6 w-6 text-coral-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">{item.label}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-amber-50 border-y border-amber-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <AlertCircle className="h-6 w-6 text-amber-600 mt-0.5" />
            </div>
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Important distinction</h3>
              <p className="text-amber-800 leading-relaxed">
                VZMO is a coaching tool, not a replacement for therapy. If you're experiencing a mental health crisis,
                severe depression, anxiety disorders, trauma, or thoughts of self-harm, please seek help from a
                licensed mental health professional. Coaching is designed for personal growth, not clinical treatment.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="when-to-use" className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Coaching is right for you if...
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              VZMO helps with personal growth and life navigation, not clinical issues.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-[#FDFBF7] rounded-2xl p-8 border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-coral-100 flex items-center justify-center mb-5">
                  <useCase.icon className="h-6 w-6 text-coral-600" />
                </div>
                <h3 className="text-xl font-heading font-bold text-slate-900 mb-3">
                  {useCase.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <DollarSign className="h-12 w-12 text-coral-500 mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              The cost comparison
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              BetterHelp costs $240-$360/month. VZMO starts at $9.99/month.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-slate-200">
              <h3 className="font-heading font-bold text-slate-900 text-xl mb-4">BetterHelp</h3>
              <div className="text-4xl font-bold text-slate-900 mb-2">$260<span className="text-lg font-normal text-slate-500">/mo avg</span></div>
              <ul className="space-y-3 text-slate-600 mt-6">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-sage-600 mt-0.5 flex-shrink-0" />
                  <span>Licensed therapist</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-sage-600 mt-0.5 flex-shrink-0" />
                  <span>Clinical treatment</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span>Requires scheduling</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span>Wait for responses</span>
                </li>
                <li className="flex items-start gap-2">
                  <X className="h-5 w-5 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span>Limited session time</span>
                </li>
              </ul>
            </div>
            <div className="bg-slate-900 rounded-2xl p-8 border border-coral-500/30">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="font-heading font-bold text-white text-xl">VZMO</h3>
                <span className="px-2 py-0.5 bg-coral-500 text-white text-xs font-medium rounded-full">96% less</span>
              </div>
              <div className="text-4xl font-bold text-white mb-2">$9.99<span className="text-lg font-normal text-slate-400">/mo</span></div>
              <ul className="space-y-3 text-slate-300 mt-6">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                  <span>AI coach available 24/7</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                  <span>Instant responses</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                  <span>Unlimited conversations</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                  <span>Goal tracking & accountability</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-coral-400 mt-0.5 flex-shrink-0" />
                  <span>Private journaling included</span>
                </li>
              </ul>
            </div>
          </div>

          <p className="text-center text-sm text-slate-500 mt-8">
            *BetterHelp pricing based on publicly available information. Actual costs may vary.
            VZMO is not a replacement for therapy when therapy is needed.
          </p>
        </div>
      </section>

      <section id="comparison" className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Feature comparison
            </h2>
            <p className="text-lg text-slate-600">
              Different tools for different needs
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-4 px-6 font-semibold text-slate-900">Feature</th>
                    <th className="text-center py-4 px-4 font-semibold text-coral-600">VZMO</th>
                    <th className="text-center py-4 px-4 font-semibold text-slate-500">BetterHelp</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className="py-4 px-6 text-slate-700">{row.feature}</td>
                      <td className="py-4 px-4 text-center">
                        {row.vzmo ? (
                          <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center mx-auto">
                            <Check className="h-4 w-4 text-sage-600" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                            <X className="h-4 w-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {row.betterhelp ? (
                          <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center mx-auto">
                            <Check className="h-4 w-4 text-sage-600" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                            <X className="h-4 w-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Real experiences
            </h2>
            <p className="text-lg text-slate-600">
              From people who found coaching was what they needed
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-[#FDFBF7] rounded-2xl p-8 border border-slate-100">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Sparkles key={i} className="h-4 w-4 text-coral-400" />
                  ))}
                </div>
                <blockquote className="text-slate-700 leading-relaxed mb-6">
                  "{testimonial.quote}"
                </blockquote>
                <div>
                  <p className="font-semibold text-slate-900">{testimonial.author}</p>
                  <p className="text-sm text-slate-500">{testimonial.role}</p>
                  <p className="text-xs text-coral-600 mt-1">{testimonial.note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Common questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: "When should I choose therapy over coaching?",
                a: "Choose therapy if you're dealing with clinical mental health issues like depression, anxiety disorders, trauma, PTSD, addiction, or thoughts of self-harm. Coaching is for personal growth, goal achievement, and life navigation when you're generally mentally healthy but want guidance."
              },
              {
                q: "Can I use VZMO while also seeing a therapist?",
                a: "Yes! Many people use VZMO alongside therapy. Use your therapist for clinical issues and VZMO for day-to-day goal tracking, decision-making, and journaling between therapy sessions."
              },
              {
                q: "Is an AI really helpful for personal guidance?",
                a: "For the right use cases, yes. AI coaching excels at helping you think through decisions, stay accountable to goals, and process thoughts through journaling. It's available 24/7 and remembers your entire history."
              },
              {
                q: "Why is VZMO so much cheaper than BetterHelp?",
                a: "BetterHelp employs licensed therapists who require significant compensation. VZMO uses AI technology to provide coaching at scale. Different tools, different costs, different purposes."
              },
              {
                q: "What if I realize I need therapy instead?",
                a: "That's totally okay. VZMO can actually help you recognize when you need professional help. We encourage users to seek therapy when appropriate and don't try to replace clinical care."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            See if coaching is right for you
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            7-day free trial. No credit card required. No pressure.
          </p>

          <Link
            to="/signup"
            className="inline-flex items-center px-10 py-4 bg-coral-500 text-white rounded-full font-semibold text-lg hover:bg-coral-600 transition-all shadow-lg"
          >
            Start your free trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>

          <p className="text-slate-400 text-sm mt-6">
            For personal growth and life navigation. Not a replacement for therapy.
          </p>
        </div>
      </section>
    </div>
  );
}
